import React, { useState, useRef, useEffect } from "react";
import { useCartStore, useStore } from "../../store/useStore";
import styled, { keyframes } from "styled-components";
import { FaStar, FaRegStar, FaTimes, FaShareAlt } from "react-icons/fa";
import ImageZoom from "./ImageZoom";
import LightboxModal from "./LightboxModal";

export interface Product {
  id: string;
  name: string;
  price: number;
  shortDescription: string;
  longDescription: string;
  images: string[];
  inStock: boolean;
  rating: number;
  reviewsCount: number;
  materials?: string;
  size?: string;
  idealFor?: string;
}

interface ProductViewProps {
  product: Product;
}

const ProductViewSection = styled.section`
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  position: relative;
  background: none;
  border-radius: 0;
  box-shadow: none;
  padding-bottom: 1.5rem;

  @media (max-width: 768px) {
    padding-bottom: 1rem;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2.5rem;
  padding: 0.5rem 2.5rem 1.5rem 2.5rem;

  @media (max-width: 1024px) {
    gap: 2rem;
    padding: 0.5rem 2rem 1.5rem 2rem;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
    padding: 1rem;
  }

  @media (max-width: 480px) {
    gap: 1rem;
    padding: 0.5rem;
  }
`;

const ImagesCol = styled.div`
  display: flex;
  max-width: 650px;
  flex-direction: row;
  gap: 1rem;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex: none;
    width: 100%;
    flex-direction: column;
    gap: 0;
  }
`;

const MainImageWrapper = styled.div`
  flex: 1;
  aspect-ratio: 1/1;
  background: #f7f7f7;
  border-radius: 1rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  touch-action: pan-y;
  -webkit-tap-highlight-color: transparent;
  user-select: none;

  @media (max-width: 768px) {
    border-radius: 0.8rem;
    margin-bottom: 1rem;
    width: 100%;
    flex: none;
    touch-action: pan-x;
    overflow: hidden;
  }

  @media (max-width: 480px) {
    border-radius: 0.5rem;
    margin-bottom: 0.8rem;
  }
`;

const ImageSlider = styled.div<{
  translateX: number;
  isTransitioning: boolean;
}>`
  display: flex;
  width: 100%;
  height: 100%;
  transform: translateX(${(props) => props.translateX}px);
  transition: ${(props) =>
    props.isTransitioning
      ? "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      : "none"};

  @media (min-width: 769px) {
    transform: translateX(0);
    transition: none;
  }
`;

const SlideImage = styled.div`
  min-width: 100%;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MainImage = styled.img<{ fade: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 1rem;
  opacity: ${(p) => (p.fade ? 0.5 : 1)};
  transition: opacity 0.2s ease-in-out;
  cursor: pointer;

  @media (max-width: 768px) {
    border-radius: 0.8rem;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 480px) {
    border-radius: 0.5rem;
  }
`;

const Thumbnails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  width: 80px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Thumb = styled.img<{ selected: boolean }>`
  width: 70px;
  height: 70px;
  object-fit: cover;
  border-radius: 0.6rem;
  border: 2px solid ${(p) => (p.selected ? "#e97d7dff" : "#eee")};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fff;

  &:hover {
    border-color: #e97d7dff;
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(233, 125, 125, 0.2);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const MobileDots = styled.div`
  display: none;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const Dot = styled.div<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => (p.active ? "#e97d7dff" : "#ccc")};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e97d7dff;
    transform: scale(1.2);
  }
`;

const DetailsCol = styled.div`
  flex: 1.3;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;

  @media (max-width: 768px) {
    flex: none;
    width: 100%;
    gap: 1rem;
  }

  @media (max-width: 480px) {
    gap: 0.8rem;
  }
`;

const ProductName = styled.h2`
  font-size: 2.1rem;
  font-family: sans-serif;
  font-weight: 700;
  margin: 0 0 0.3rem 0;
  color: #222;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const Price = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: #e97d7dff;
  margin-bottom: 0.2rem;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const ShortDesc = styled.div`
  font-size: 1.1rem;
  color: #444;
  margin-bottom: 0.2rem;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const LongDescToggle = styled.button`
  background: none;
  border: none;
  color: #e97d7dff;
  font-size: 1rem;
  cursor: pointer;
  margin-bottom: 0.2rem;
  text-align: left;
  &:hover {
    text-decoration: underline;
  }
`;

const LongDesc = styled.div`
  font-size: 1rem;
  color: #333;
  margin-bottom: 0.2rem;
  background: #f9f9f9;
  border-radius: 0.7rem;
  padding: 0.7rem 1rem;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.6rem 0.8rem;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0.5rem 0.7rem;
    border-radius: 0.5rem;
  }
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0.7rem 0;
  color: #555;
  font-size: 1rem;
  li {
    margin-bottom: 0.2rem;
  }
`;

const StockStatus = styled.div<{ inStock: boolean }>`
  font-size: 1rem;
  color: ${(p) => (p.inStock ? "#1ca97a" : "#e97d7dff")};
  font-weight: 600;
  margin-bottom: 0.2rem;
`;

const Ratings = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 1.1rem;
  color: #e97d7dff;

  @media (max-width: 768px) {
    justify-content: center;
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    gap: 0.3rem;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  margin: 1.1rem 0 0.5rem 0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  @media (max-width: 480px) {
    gap: 0.8rem;
    margin: 1rem 0 0.5rem 0;
  }
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  border: 1.5px solid #eee;
  border-radius: 1.5rem;
  overflow: hidden;
  background: #fafafa;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);

  @media (max-width: 768px) {
    justify-content: center;
    width: 100%;
  }
`;

const QtyBtn = styled.button`
  background: none;
  border: none;
  color: #e97d7dff;
  font-size: 1.3rem;
  width: 2.2rem;
  height: 2.2rem;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: #f2eaea;
  }
`;

const QtyValue = styled.div`
  min-width: 2.2rem;
  text-align: center;
  font-size: 1.1rem;
  color: #333;
  font-weight: 500;
`;

const AddToCartBtn = styled.button`
  background: #e97d7dff;
  color: #fff;
  border: none;
  border-radius: 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.8rem 2.2rem;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(233, 125, 125, 0.08);
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: #fff;
    color: #e97d7dff;
    border: 2px solid #e97d7dff;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem 2.2rem;
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.9rem 1.5rem;
    font-size: 0.95rem;
  }
`;

const BuyNowBtn = styled.button`
  background: #fff;
  color: #e97d7dff;
  border: 2px solid #e97d7dff;
  border-radius: 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.8rem 2.2rem;
  cursor: pointer;
  margin-left: 0.2rem;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: #e97d7dff;
    color: #fff;
  }

  @media (max-width: 768px) {
    width: 100%;
    margin-left: 0;
    padding: 1rem 2.2rem;
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.9rem 1.5rem;
    font-size: 0.95rem;
  }
`;

const SocialShare = styled.div`
  margin-top: 0.7rem;
  display: flex;
  gap: 1.1rem;
  align-items: center;
  color: #e97d7dff;
  font-size: 1.3rem;
  cursor: pointer;

  @media (max-width: 768px) {
    justify-content: center;
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 1.2rem;
    font-size: 1.2rem;
  }
`;

const SuggestedProducts = styled.div`
  margin: 2.5rem 0 1.2rem 0;
  padding: 1.2rem 0 0 0;
  border-top: 1.5px solid #f0eaea;
  color: #888;
  text-align: center;
  font-size: 1.1rem;

  @media (max-width: 768px) {
    margin: 2rem 0 1rem 0;
    padding: 1rem 0 0 0;
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    margin: 1.5rem 0 0.8rem 0;
    padding: 0.8rem 0 0 0;
    font-size: 0.9rem;
  }
`;

const HoverHint = styled.div`
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: #999;
  text-align: center;
  font-style: italic;
`;

export const ProductView: React.FC<ProductViewProps> = ({ product }) => {
  const [mainImg, setMainImg] = useState(0);
  const [imgFade, setImgFade] = useState(false);
  const [qty, setQty] = useState(1);
  const [showLong, setShowLong] = useState(false);
  const [hoveredThumb, setHoveredThumb] = useState<number | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Touch/swipe functionality for mobile
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [translateX, setTranslateX] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const imageRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  // Minimum distance for swipe
  const minSwipeDistance = 50;

  const handleThumbClick = (idx: number) => {
    if (mainImg !== idx) {
      setImgFade(true);
      setTimeout(() => {
        setMainImg(idx);
        setImgFade(false);
      }, 180);
    }
  };

  const handleThumbHover = (idx: number) => {
    setHoveredThumb(idx);
    if (mainImg !== idx) {
      setImgFade(true);
      setTimeout(() => {
        setMainImg(idx);
        setImgFade(false);
      }, 100);
    }
  };

  const handleThumbLeave = () => {
    setHoveredThumb(null);
  };

  // Touch event handlers for mobile swipe with smooth sliding
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    const touch = e.targetTouches[0];
    setTouchStart(touch.clientX);
    setIsTransitioning(false);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const touch = e.targetTouches[0];
    setTouchEnd(touch.clientX);

    // Calculate real-time slide position for smooth dragging (mobile only)
    if (isMobile && touchStart !== null && sliderRef.current) {
      const containerWidth = sliderRef.current.offsetWidth;
      const deltaX = touch.clientX - touchStart;
      const baseTranslate = -mainImg * containerWidth;
      const newTranslateX = baseTranslate + deltaX;

      // Limit the slide to prevent over-scrolling
      const minTranslate = -(product.images.length - 1) * containerWidth;
      const maxTranslate = 0;
      const clampedTranslate = Math.max(
        minTranslate,
        Math.min(maxTranslate, newTranslateX)
      );

      setTranslateX(clampedTranslate);
    }
  };

  const onTouchEnd = () => {
    if (!isMobile) return;

    if (touchStart === null || touchEnd === null || !sliderRef.current) {
      setTranslateX(-mainImg * (sliderRef.current?.offsetWidth || 0));
      return;
    }

    const containerWidth = sliderRef.current.offsetWidth;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    setIsTransitioning(true);

    if (isLeftSwipe && mainImg < product.images.length - 1) {
      // Swipe left - next image
      const nextIndex = mainImg + 1;
      setMainImg(nextIndex);
      setTranslateX(-nextIndex * containerWidth);
    } else if (isRightSwipe && mainImg > 0) {
      // Swipe right - previous image
      const prevIndex = mainImg - 1;
      setMainImg(prevIndex);
      setTranslateX(-prevIndex * containerWidth);
    } else {
      // Snap back to current image if swipe wasn't strong enough
      setTranslateX(-mainImg * containerWidth);
    }

    // Reset touch states
    setTouchStart(null);
    setTouchEnd(null);

    // Reset transition flag after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  };

  // Initialize translateX on mount and when mainImg changes (mobile only)
  useEffect(() => {
    if (!isMobile) return;

    const updateTranslateX = () => {
      if (sliderRef.current) {
        const containerWidth = sliderRef.current.offsetWidth;
        setIsTransitioning(true);
        setTranslateX(-mainImg * containerWidth);
        setTimeout(() => {
          setIsTransitioning(false);
        }, 300);
      }
    };

    // Small delay to ensure sliderRef is available
    const timer = setTimeout(updateTranslateX, 50);
    return () => clearTimeout(timer);
  }, [mainImg, isMobile]);

  // Track window resize for mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const addToCart = useCartStore((state) => state.addToCart);
  const setCartOpen = useStore((state) => state.setCartOpen);
  return (
    <ProductViewSection>
      <Content>
        <ImagesCol>
          <Thumbnails>
            {product.images.map((img, i) => (
              <Thumb
                key={img}
                src={img}
                selected={i === mainImg}
                onClick={() => handleThumbClick(i)}
                onMouseEnter={() => handleThumbHover(i)}
                onMouseLeave={handleThumbLeave}
                alt={product.name + " thumbnail " + (i + 1)}
              />
            ))}
          </Thumbnails>
          <MainImageWrapper
            ref={imageRef}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {isMobile ? (
              <ImageSlider
                ref={sliderRef}
                translateX={translateX}
                isTransitioning={isTransitioning}
              >
                {product.images.map((img, index) => (
                  <SlideImage key={index}>
                    <ImageZoom
                      src={img}
                      alt={`${product.name} - Image ${index + 1}`}
                      onClick={() => setLightboxOpen(true)}
                    />
                  </SlideImage>
                ))}
              </ImageSlider>
            ) : (
              <ImageZoom
                src={product.images[mainImg]}
                alt={product.name}
                onClick={() => setLightboxOpen(true)}
              />
            )}
          </MainImageWrapper>
          <MobileDots>
            {product.images.map((_, i) => (
              <Dot
                key={i}
                active={i === mainImg}
                onClick={() => handleThumbClick(i)}
              />
            ))}
          </MobileDots>
        </ImagesCol>
        <DetailsCol>
          <ProductName>{product.name}</ProductName>
          {/* <Ratings>
            {Array.from({ length: 5 }).map((_, i) =>
              i < Math.round(product.rating) ? (
                <span key={i}>{FaStar({}) as React.ReactElement}</span>
              ) : (
                <span key={i}>{FaRegStar({}) as React.ReactElement}</span>
              )
            )}
            <span style={{ color: "#888", fontSize: "1rem", marginLeft: 6 }}>
              ({product.reviewsCount} reviews)
            </span>
          </Ratings> */}
          <Price>${product.price.toFixed(2)}</Price>
          <ShortDesc>{product.shortDescription}</ShortDesc>
          <LongDescToggle onClick={() => setShowLong((v) => !v)}>
            {showLong ? "Hide details" : "Show more"}
          </LongDescToggle>
          {showLong && <LongDesc>{product.longDescription}</LongDesc>}
          <InfoList>
            {product.materials && (
              <li>
                <b>Materials:</b> {product.materials}
              </li>
            )}
            {product.size && (
              <li>
                <b>Size:</b> {product.size}
              </li>
            )}
            {product.idealFor && (
              <li>
                <b>Ideal For:</b> {product.idealFor}
              </li>
            )}
          </InfoList>
          <StockStatus inStock={product.inStock}>
            {product.inStock
              ? product.inStock === true
                ? "In Stock"
                : `Only ${product.inStock} left`
              : "Out of Stock"}
          </StockStatus>
          <ActionsRow>
            <QuantitySelector>
              <QtyBtn onClick={() => setQty((q) => Math.max(1, q - 1))}>
                -
              </QtyBtn>
              <QtyValue>{qty}</QtyValue>
              <QtyBtn onClick={() => setQty((q) => q + 1)}>+</QtyBtn>
            </QuantitySelector>
            <AddToCartBtn
              onClick={() => {
                addToCart({
                  id: product.id,
                  name: product.name,
                  price: product.price,
                  qty,
                  images: product.images,
                });
                setCartOpen(true);
              }}
            >
              Add to Cart
            </AddToCartBtn>
          </ActionsRow>
        </DetailsCol>
      </Content>
      <SuggestedProducts />

      <LightboxModal
        images={product.images}
        currentIndex={mainImg}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNavigate={(index) => setMainImg(index)}
      />
    </ProductViewSection>
  );
};
